import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useRef } from 'react';
import UsersAutocomplete from '../../../shared/users-autocomplete';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import AvatarChip from '../../../shared/avatar-chip';
import z from 'zod';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

interface DuplicateUserGroupsActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
}

export const DuplicateUserGroupsActionSchema = z.object({
  type: z.literal('Duplicate_User_Groups'),
  srcUser: z.string().min(1, 'A user is required.'),
});

export type DuplicateUserGroupsActionType = z.infer<
  typeof DuplicateUserGroupsActionSchema
>;

// Type guard to check if an action is DuplicateUserGroupsActionType
function isDuplicateUserGroupsAction(
  action: PolicyAction
): action is DuplicateUserGroupsActionType {
  return action.type === 'Duplicate_User_Groups';
}

export default function DuplicateUserGroupsAction({
  setValue,
  watch,
  errors,
  control,
}: DuplicateUserGroupsActionProps) {
  const actionData = watch('actionData');
  const autocompleteKey = useRef(new Date().getTime().toString());
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Duplicate_User_Groups');
  const actionAtIndex = actionData[idx];
  if (!isDuplicateUserGroupsAction(actionAtIndex)) {
    return null;
  }

  const currentAction: DuplicateUserGroupsActionType = actionAtIndex;
  const error: FieldErrors<DuplicateUserGroupsActionType> | undefined =
    errors?.actionData?.[idx];

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={1}>
        <Box sx={{ width: 400 }}>
          {currentAction.srcUser && currentAction.srcUser.length > 0 ? (
            <AvatarChip
              type="User"
              email={currentAction.srcUser}
              onDelete={() => setValue(`actionData.${idx}.srcUser`, '')}
              size="medium"
            />
          ) : (
            <Controller
              name={`actionData.${idx}.srcUser`}
              control={control}
              render={() => (
                <UsersAutocomplete
                  newKey={`${autocompleteKey.current}`}
                  autocompleteValue={currentAction.srcUser}
                  setAutoCompleteValue={(newValue) =>
                    setValue(`actionData.${idx}.srcUser`, newValue, {
                      shouldValidate: true,
                    })
                  }
                  errors={!!error?.srcUser}
                  helperText={error?.srcUser?.message || ' '}
                />
              )}
            />
          )}
        </Box>
      </Stack>
    </Stack>
  );
}
